import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

interface UserProfile {
  id: string;
  avatar_url: string;
  full_name: string;
  role?: string;
}

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setUser(user);
        
        if (user) {
          // Fetch user profile with avatar
          const { data: profile } = await supabase
            .from('profiles')
            .select('id, avatar_url, full_name, role')
            .eq('id', user.id)
            .single();
          
          if (profile) {
            setUserProfile(profile);
          }

          // Check if user is admin
          const adminEmails = ['admin@pepcoin.com', 'support@pepcoin.com'];
          const isAdminUser = adminEmails.includes(user.email) || profile?.role === 'admin';
          setIsAdmin(isAdminUser);
        }
      } catch (error) {
        console.error('Error checking user:', error);
      }
    };
    checkUser();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        setUser(session.user);
        
        // Fetch profile for new user
        const { data: profile } = await supabase
          .from('profiles')
          .select('id, avatar_url, full_name, role')
          .eq('id', session.user.id)
          .single();
        
        if (profile) {
          setUserProfile(profile);
        }

        // Check admin status
        const adminEmails = ['admin@pepcoin.com', 'support@pepcoin.com'];
        const isAdminUser = adminEmails.includes(session.user.email) || profile?.role === 'admin';
        setIsAdmin(isAdminUser);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setUserProfile(null);
        setIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Listen for profile updates
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('profile-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`
        },
        (payload) => {
          if (payload.new) {
            const newProfile = payload.new as UserProfile;
            setUserProfile(newProfile);
            
            // Update admin status if role changed
            const adminEmails = ['admin@pepcoin.com', 'support@pepcoin.com'];
            const isAdminUser = adminEmails.includes(user.email) || newProfile?.role === 'admin';
            setIsAdmin(isAdminUser);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setUserProfile(null);
      setIsAdmin(false);
      setShowUserMenu(false);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Consolidated navigation items with icons
  const navItems = [
    { path: '/', label: 'Home', icon: 'ri-home-4-fill' },
    { path: '/trading', label: 'Trade', icon: 'ri-line-chart-fill' },
    { path: '/wallet', label: 'Wallet', icon: 'ri-wallet-3-fill' },
    { path: '/p2p', label: 'P2P', icon: 'ri-exchange-fill' },
    { path: '/arbitrage', label: 'Arbitrage', icon: 'ri-swap-line' },
    { path: '/mining', label: 'Mine', icon: 'ri-cpu-fill' },
    { path: '/robot', label: 'AI Bot', icon: 'ri-robot-fill' },
    { path: '/investment', label: 'Invest', icon: 'ri-funds-fill' },
    { path: '/pepverse', label: 'Verse', icon: 'ri-global-fill' },
    { path: '/game', label: 'Game', icon: 'ri-gamepad-fill' }
  ];

  // Consolidated tools with icons only
  const toolItems = [
    { path: '/payment/manual', label: 'Pep-Pay', icon: 'ri-hand-coin-fill', color: 'text-orange-400' },
    { path: '/kyc', label: 'KYC', icon: 'ri-shield-check-fill', color: 'text-green-400' },
    { path: '/referral', label: 'Refer', icon: 'ri-share-fill', color: 'text-purple-400' },
    { path: '/help', label: 'Help', icon: 'ri-customer-service-fill', color: 'text-blue-400' },
    { path: 'https://t.me/+20f3SQXmKYZkZWU0', label: 'Telegram', icon: 'ri-telegram-fill', color: 'text-sky-400', external: true }
  ];

  const isActive = (path) => location.pathname === path;

  const renderUserAvatar = () => {
    if (userProfile?.avatar_url) {
      return (
        <img
          src={userProfile.avatar_url}
          alt={userProfile.full_name || 'Profile'}
          className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover border-2 border-white/20"
          onError={(e) => {
            // Fallback to initials if image fails to load
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const fallback = target.nextElementSibling as HTMLElement;
            if (fallback) {
              fallback.style.display = 'flex';
            }
          }}
        />
      );
    }
    
    return (
      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-cyan-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-xs sm:text-sm">
        {userProfile?.full_name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
      </div>
    );
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-white/10">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Logo - Using Authentic PepCoin Logo with Fallback */}
          <Link to="/" className="flex items-center gap-2">
            <div className="relative w-7 h-7 sm:w-8 sm:h-8">
              <img 
                src="https://static.readdy.ai/image/58c34b0b112df76c80b184053cb893a8/8c747b92224cceee656af4128cf2cd52.png" 
                alt="PepCoin Logo" 
                className="w-full h-full object-contain"
                onError={(e) => {
                  // Fallback to a styled text logo if image fails
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) {
                    fallback.style.display = 'flex';
                  }
                }}
              />
              {/* Fallback Logo */}
              <div 
                className="w-full h-full bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm"
                style={{ display: 'none' }}
              >
                P
              </div>
            </div>
            <span className="pepcoin-nav-logo text-white font-bold text-sm sm:text-lg hidden sm:block">
              PepCoin
            </span>
          </Link>

          {/* Desktop Navigation - Icons Only */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`w-9 h-9 xl:w-10 xl:h-10 flex items-center justify-center rounded-lg transition-all duration-300 cursor-pointer group relative ${
                  isActive(item.path)
                    ? 'bg-cyan-500/20 text-cyan-400'
                    : 'text-gray-400 hover:text-white hover:bg-white/10'
                }`}
                title={item.label}
              >
                <i className={`${item.icon} text-base xl:text-lg`}></i>
                {/* Tooltip */}
                <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-black/90 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  {item.label}
                </div>
              </Link>
            ))}
          </div>

          {/* Tools & User Section - Mobile Optimized */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Tools - Icons Only - Hidden on small mobile */}
            <div className="hidden sm:flex items-center gap-1">
              {toolItems.map((tool) => (
                tool.external ? (
                  <a
                    key={tool.path}
                    href={tool.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg transition-all duration-300 cursor-pointer group relative ${tool.color} hover:bg-white/10`}
                    title={tool.label}
                  >
                    <i className={`${tool.icon} text-xs sm:text-sm`}></i>
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-black/90 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      {tool.label}
                    </div>
                  </a>
                ) : (
                  <Link
                    key={tool.path}
                    to={tool.path}
                    className={`w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-lg transition-all duration-300 cursor-pointer group relative ${tool.color} hover:bg-white/10`}
                    title={tool.label}
                  >
                    <i className={`${tool.icon} text-xs sm:text-sm`}></i>
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-black/90 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      {tool.label}
                    </div>
                  </Link>
                )
              ))}
            </div>

            {/* User Menu - Mobile Optimized */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="relative hover:scale-105 transition-all duration-300 cursor-pointer"
                >
                  {renderUserAvatar()}
                  {/* Fallback initials (hidden by default) */}
                  <div 
                    className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-cyan-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-xs sm:text-sm"
                    style={{ display: 'none' }}
                  >
                    {userProfile?.full_name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  
                  {/* Online indicator */}
                  <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 border-2 border-black rounded-full"></div>
                  
                  {/* Admin badge */}
                  {isAdmin && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-purple-500 border border-black rounded-full flex items-center justify-center">
                      <i className="ri-vip-crown-fill text-white text-xs"></i>
                    </div>
                  )}
                </button>
                
                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-2 w-48 sm:w-56 bg-black/90 backdrop-blur-md border border-white/20 rounded-xl py-2 shadow-xl">
                    {/* User Info Header */}
                    <div className="px-3 sm:px-4 py-2 sm:py-3 border-b border-white/10">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="relative">
                          {renderUserAvatar()}
                          {isAdmin && (
                            <div className="absolute -top-1 -right-1 w-3 h-3 bg-purple-500 border border-black rounded-full flex items-center justify-center">
                              <i className="ri-vip-crown-fill text-white text-xs"></i>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium text-xs sm:text-sm truncate">
                            {userProfile?.full_name || 'User'}
                            {isAdmin && <span className="text-purple-400 ml-1">(Admin)</span>}
                          </p>
                          <p className="text-gray-400 text-xs truncate">
                            {user?.email}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Menu Items */}
                    <div className="py-1">
                      <Link
                        to="/profile"
                        className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <i className="ri-user-fill text-cyan-400 text-sm"></i>
                        <span className="text-xs sm:text-sm">Profile Settings</span>
                      </Link>
                      <Link
                        to="/wallet"
                        className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <i className="ri-wallet-fill text-green-400 text-sm"></i>
                        <span className="text-xs sm:text-sm">My Wallet</span>
                      </Link>
                      
                      {/* Admin Dashboard - Only show for admin users */}
                      {isAdmin && (
                        <Link
                          to="/admin"
                          className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <i className="ri-dashboard-fill text-purple-400 text-sm"></i>
                          <span className="text-xs sm:text-sm">Admin Dashboard</span>
                        </Link>
                      )}
                      
                      <div className="border-t border-white/10 my-1"></div>
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 text-gray-300 hover:text-white hover:bg-white/10 transition-colors text-left"
                      >
                        <i className="ri-logout-box-fill text-red-400 text-sm"></i>
                        <span className="text-xs sm:text-sm">Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-1 sm:gap-2">
                <Link
                  to="/auth/signin"
                  className="px-2 sm:px-3 py-1.5 text-gray-300 hover:text-white transition-colors text-xs sm:text-sm"
                >
                  Sign In
                </Link>
                <Link
                  to="/auth/signup"
                  className="px-2 sm:px-3 py-1.5 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg text-white text-xs sm:text-sm font-medium hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Sign Up
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-white hover:bg-white/10 rounded-lg transition-colors ml-1"
            >
              <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-sm sm:text-lg`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu - Optimized */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-white/10 py-3 sm:py-4">
            {/* User Info in Mobile Menu */}
            {user && (
              <div className="mb-3 sm:mb-4 px-2 sm:px-3 py-2 sm:py-3 bg-white/5 rounded-lg">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="relative">
                    {renderUserAvatar()}
                    {isAdmin && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-purple-500 border border-black rounded-full flex items-center justify-center">
                        <i className="ri-vip-crown-fill text-white text-xs"></i>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-xs sm:text-sm truncate">
                      {userProfile?.full_name || 'User'}
                      {isAdmin && <span className="text-purple-400 ml-1">(Admin)</span>}
                    </p>
                    <p className="text-gray-400 text-xs truncate">
                      {user?.email}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Navigation Grid - Mobile Optimized */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-3 sm:mb-4">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex flex-col items-center gap-1 p-2 sm:p-3 rounded-lg transition-all duration-300 cursor-pointer ${
                    isActive(item.path)
                      ? 'bg-cyan-500/20 text-cyan-400'
                      : 'text-gray-400 hover:text-white hover:bg-white/10'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className={`${item.icon} text-base sm:text-lg`}></i>
                  <span className="text-xs">{item.label}</span>
                </Link>
              ))}
            </div>
            
            {/* Tools Grid - Mobile Optimized */}
            <div className="grid grid-cols-4 gap-2 pt-2 sm:pt-3 border-t border-white/10">
              {toolItems.map((tool) => (
                tool.external ? (
                  <a
                    key={tool.path}
                    href={tool.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all duration-300 cursor-pointer ${tool.color} hover:bg-white/10`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <i className={`${tool.icon} text-sm`}></i>
                    <span className="text-xs">{tool.label}</span>
                  </a>
                ) : (
                  <Link
                    key={tool.path}
                    to={tool.path}
                    className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all duration-300 cursor-pointer ${tool.color} hover:bg-white/10`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <i className={`${tool.icon} text-sm`}></i>
                    <span className="text-xs">{tool.label}</span>
                  </Link>
                )
              ))}
              
              {/* Admin Dashboard in Mobile Menu - Only for admins */}
              {isAdmin && (
                <Link
                  to="/admin"
                  className="flex flex-col items-center gap-1 p-2 rounded-lg transition-all duration-300 cursor-pointer text-purple-400 hover:bg-white/10"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="ri-dashboard-fill text-sm"></i>
                  <span className="text-xs">Admin</span>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

// Add named export for compatibility
export { Navbar };
